<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { $ } from '@@/bits/jquery'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { computeAndSaveNewPostIndex } from '@@/surface/sorter'
import type { Id, Post } from '@@/types'
import SurfacePostCanvas from '@@/vuecomponents/SurfacePostCanvas.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { useSurfaceGridShapeshift } from '@@/vuecomposables/surface_grid_shapeshift'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'

const props = defineProps<{
  wallContainer: HTMLDivElement
  reactionIdsByPostId: Record<Id, Id[]>
  commentIdsByPostId: Record<Id, Id[]>
  posts: Post[]
}>()

if (!device.mobile) useDoubleClickToPost()

const wallContainerRef = computed(() => props.wallContainer)
useSurfaceGridShapeshift({
  wallContainerRef,
  options: {
    rtl: dir() === 'rtl',
  },
  onPostRearranged,
})

const { isPostSizeWide } = storeToRefs(useSurfaceStore())
const { isAPostDragging } = storeToRefs(useSurfacePostsDragAndDropStore())
const { surfaceContainerWidth, isContainerSmallerThanTabletLandscape } = storeToRefs(useSurfaceContainerSizeStore())
const { shouldDisplayPost } = useSurfacePostsStore()
const { setSnackbar } = useGlobalSnackbarStore()
const { windowHeight } = storeToRefs(useWindowSizeStore())

const { isReactable, isCommentable } = storeToRefs(useSurfaceSettingsStore())

const rootElement: Ref<HTMLDivElement | null> = ref(null)
const internalAllSortedPosts = ref<Post[]>([])

const maxPostWidth = computed((): number => {
  return isPostSizeWide.value ? 464 : 280
})

const postWidth = computed(() => {
  return isPostSizeWide.value ? widePostWidth.value : standardPostWidth.value
})

const minPostHeight = computed(() => {
  // Old measurements for reference
  // const minimumPostHeightWithComments = 78
  // const minimumPostHeightWithoutComments = 42
  // return xCommentsUnderPosts.value ? minimumPostHeightWithComments : minimumPostHeightWithoutComments

  // New measurements are estimates assuming single line body text.
  // It doesn't fully eliminate layout shift but it's a good starting point to reduce jankiness.
  const minimumPostHeightWithoutCommentsOrReactions = 77
  const minimumPostHeightWithOnlyReactions = 107.5
  const minimumPostHeightWithOnlyComments = 117.5
  const minimumPostHeightWithCommentsAndReactions = 143.5

  if (isCommentable.value && isReactable.value) {
    return minimumPostHeightWithCommentsAndReactions
  } else if (isCommentable.value) {
    return minimumPostHeightWithOnlyComments
  } else if (isReactable.value) {
    return minimumPostHeightWithOnlyReactions
  } else {
    return minimumPostHeightWithoutCommentsOrReactions
  }
})

function calculateMinPostHeight(post: Post): number {
  // If no post provided or no image dimensions available, return default height
  if (!post?.attachment_link?.preview_image?.height || !post?.attachment_link?.preview_image?.width) {
    return minPostHeight.value
  }

  // If attachment is present
  const imageHeight = post.attachment_link.preview_image.height
  const imageWidth = post.attachment_link.preview_image.width
  const aspectRatio = imageHeight / imageWidth

  // Calculate height based on min post width and aspect ratio
  // but ensure it doesn't go below minimum height
  const estimatedAttachmentHeight = minPostWidth.value * aspectRatio
  const calculatedHeight = minPostHeight.value + estimatedAttachmentHeight
  const minimumHeight = windowHeight.value / 4 // Set a reasonable min height

  return Math.max(calculatedHeight, minimumHeight)
}

const minPostWidth = computed((): number => {
  const sidePanelWidth = isContainerSmallerThanTabletLandscape.value ? 0 : 72 // 72 is the size of the settings side panel
  const availableWidth = surfaceContainerWidth.value - sidePanelWidth - 14 // 14 is the scrollbar width

  if (!isPostSizeWide.value) {
    if (surfaceContainerWidth.value <= 584) {
      const wishListMargin = 8
      const padding = 8
      return (availableWidth - 2 * padding) / 2 - (3 * wishListMargin) / 2
    } else if (surfaceContainerWidth.value <= 904) {
      const wishListMargin = 8
      const padding = 8
      return (availableWidth - 2 * padding) / 3 - (4 * wishListMargin) / 3
    } else if (surfaceContainerWidth.value <= 1272) {
      const wishListMargin = 16
      const padding = 16
      return (availableWidth - 2 * padding) / 4 - (5 * wishListMargin) / 4
    } else if (surfaceContainerWidth.value <= 1568) {
      const wishListMargin = 16
      const padding = 16
      return (availableWidth - 2 * padding) / 5 - (6 * wishListMargin) / 5
    } else if (surfaceContainerWidth.value <= 1864) {
      const wishListMargin = 16
      const padding = 16
      return (availableWidth - 2 * padding) / 6 - (7 * wishListMargin) / 6
    } else if (surfaceContainerWidth.value <= 2160) {
      const wishListMargin = 16
      const padding = 16
      return (availableWidth - 2 * padding) / 7 - (8 * wishListMargin) / 7
    } else {
      const wishListMargin = 16
      const padding = 16
      return (availableWidth - 2 * padding) / 8 - (8 * wishListMargin) / 7
    }
  }

  if (surfaceContainerWidth.value <= 480) {
    const wishListMargin = 8
    const padding = 8
    return availableWidth - 2 * padding - 2 * wishListMargin
  } else if (surfaceContainerWidth.value <= 984) {
    const wishListMargin = 8
    const padding = 8
    return (availableWidth - 2 * padding) / 2 - (3 * wishListMargin) / 2
  } else if (surfaceContainerWidth.value <= 1528) {
    const wishListMargin = 16
    const padding = 16
    return (availableWidth - 2 * padding) / 3 - (4 * wishListMargin) / 3
  } else if (surfaceContainerWidth.value <= 2008) {
    const wishListMargin = 16
    const padding = 16
    return (availableWidth - 2 * padding) / 4 - (5 * wishListMargin) / 4
  } else if (surfaceContainerWidth.value <= 2488) {
    const wishListMargin = 16
    const padding = 16
    return (availableWidth - 2 * padding) / 5 - (6 * wishListMargin) / 5
  } else if (surfaceContainerWidth.value <= 2968) {
    const wishListMargin = 16
    const padding = 16
    return (availableWidth - 2 * padding) / 6 - (7 * wishListMargin) / 6
  } else if (surfaceContainerWidth.value <= 3448) {
    const wishListMargin = 16
    const padding = 16
    return (availableWidth - 2 * padding) / 7 - (8 * wishListMargin) / 7
  } else {
    const wishListMargin = 16
    const padding = 16
    return (availableWidth - 2 * padding) / 8 - (9 * wishListMargin) / 7
  }
})

const widePostWidth = computed((): string => {
  if (surfaceContainerWidth.value <= 480) {
    const wishListMargin = 8
    return `calc(100% - ${(2 * wishListMargin) / 1}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 984) {
    const wishListMargin = 8
    return `calc(50% - ${(3 * wishListMargin) / 2}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 1528) {
    const wishListMargin = 16
    return `calc(33.3% - ${(4 * wishListMargin) / 3}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 2008) {
    const wishListMargin = 16
    return `calc(25% - ${(5 * wishListMargin) / 4}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 2488) {
    const wishListMargin = 16
    return `calc(20% - ${(6 * wishListMargin) / 5}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 2968) {
    const wishListMargin = 16
    return `calc(16.67% - ${(7 * wishListMargin) / 6}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 3448) {
    const wishListMargin = 16
    return `calc(14.29% - ${(8 * wishListMargin) / 7}px - 0.5px)`
  } else {
    const wishListMargin = 16
    return `calc(12.5% - ${(9 * wishListMargin) / 7}px - 0.5px)`
  }
})

const standardPostWidth = computed((): string => {
  if (surfaceContainerWidth.value <= 584) {
    const wishListMargin = 8
    return `calc(50% - ${(3 * wishListMargin) / 2}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 904) {
    const wishListMargin = 8
    return `calc(33.3% - ${(4 * wishListMargin) / 3}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 1272) {
    const wishListMargin = 16
    return `calc(25% - ${(5 * wishListMargin) / 4}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 1568) {
    const wishListMargin = 16
    return `calc(20% - ${(6 * wishListMargin) / 5}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 1864) {
    const wishListMargin = 16
    return `calc(16.67% - ${(7 * wishListMargin) / 6}px - 0.5px)`
  } else if (surfaceContainerWidth.value <= 2160) {
    const wishListMargin = 16
    return `calc(14.29% - ${(8 * wishListMargin) / 7}px - 0.5px)`
  } else {
    const wishListMargin = 16
    return `calc(12.5% - ${(8 * wishListMargin) / 7}px - 0.5px)`
  }
})

function onPostRearranged(post: HTMLElement): void {
  // Check if there is a pinned post between unpinned posts or vice versa.
  // If there is, it's an invalid drop and we should revert the post order.
  const $post = $(post)
  const $prevPost = $post.prev('.wish')
  const $nextPost = $post.next('.wish')
  if ($prevPost.length > 0 || $nextPost.length > 0) {
    const isPostPinned = $post.attr('data-pinned') === 'true'
    const isPrevPostPinned = $prevPost.attr('data-pinned') === 'true'
    const isNextPostPinned = $nextPost.attr('data-pinned') === 'true'

    const wasUnpinnedPostDroppedBeforeFirstPinnedPost = !isPostPinned && $prevPost.length === 0 && isNextPostPinned
    const wasPinnedPostDropAfterLastUnpinnedPost = isPostPinned && !isPrevPostPinned && $nextPost.length === 0
    const wasPinnedPostDroppedBetweenUnpinnedPosts = isPostPinned && !isPrevPostPinned && !isNextPostPinned
    const wasUnpinnedPostDroppedBetweenPinnedPosts = !isPostPinned && isPrevPostPinned && isNextPostPinned

    if (
      wasUnpinnedPostDroppedBeforeFirstPinnedPost ||
      wasPinnedPostDropAfterLastUnpinnedPost ||
      wasPinnedPostDroppedBetweenUnpinnedPosts ||
      wasUnpinnedPostDroppedBetweenPinnedPosts
    ) {
      resetPostOrder()
      showInvalidDragToast(isPostPinned)
      return
    }
  }

  // If the drop is valid, update post sort index as usual.
  computeAndSaveNewPostIndex(post)
}

function resetPostOrder(): void {
  // After dragging posts with shapeshift (useSurfaceGridShapeshift), the order is already
  // preserved in the DOM. To revert it, we need to re-render the v-for.
  // There are a few ways to do this but only re-assigning the array passed
  // to v-for seems to not cause a flicker/flash.
  let posts = [...internalAllSortedPosts.value]
  nextTick(() => {
    posts = [...posts.reverse()]
    internalAllSortedPosts.value = posts
    nextTick(() => {
      posts = [...posts.reverse()]
      internalAllSortedPosts.value = posts
    })
  })
}

function showInvalidDragToast(isPostPinned: boolean): void {
  const message = isPostPinned ? __('Cannot insert between unpinned posts') : __('Cannot insert between pinned posts')
  setSnackbar({
    message,
    notificationType: SnackbarNotificationType.error,
  })
}

watch(
  () => props.posts,
  (newPosts: Post[]) => {
    // Avoid updating the internal posts array when dragging cuz it can
    // mess with drag n drop.
    if (!isAPostDragging.value) internalAllSortedPosts.value = [...newPosts]
  },
)

onBeforeMount(() => {
  internalAllSortedPosts.value = [...props.posts]
})

useScrollToNewestPost(rootElement)
</script>

<template>
  <div id="wish-list" ref="rootElement" class="wish-list-masonry relative p-2 tablet-landscape:p-4" tabindex="-1">
    <template v-for="(post, index) in internalAllSortedPosts">
      <!-- We use SurfacePostCanvas for Grid (wall layout) without sections since we want them to be draggable -->
      <SurfacePostCanvas
        :key="post.cid"
        :class="{
          // Use this over v-show as it sometimes has trouble with reactivity on the shouldDisplay call
          hidden: !shouldDisplayPost(post.cid),
        }"
        :index="index"
        :post="post"
        :post-reaction-ids="reactionIdsByPostId[post.id]"
        :post-comment-ids="commentIdsByPostId[post.id]"
        :style="{
          width: postWidth,
          maxWidth: maxPostWidth + 'px',
        }"
        :lazy-render="isAppUsing('lazyRenderPosts')"
        :min-post-height="calculateMinPostHeight(post)"
        :min-post-width="minPostWidth"
      />
    </template>
  </div>
</template>
